import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../../../../utils/supabaseClient';

// Utility: build unique artists from mapping rows
function buildArtistsFromMappings(rows) {
  const byId = new Map();
  const acceptedByArtist = new Map();
  const acceptedThumbs = new Map();

  for (const row of rows) {
    const user = row.vcs?.users;
    if (!user?.uuid) continue;
    const id = user.uuid;
    const createdAt = row.created_at;

    if (!byId.has(id)) {
      byId.set(id, {
        uuid: id,
        first_name: user.first_name || null,
        last_name: user.last_name || null,
        nickname: user.nickname || null,
        email: user.email || null,
        profile_img: user.profile_img || null,
        joined_collection: createdAt,
        artworks_total: 0,
      });
    }
    const a = byId.get(id);
    a.artworks_total += 1;
    if (createdAt && (!a.joined_collection || new Date(createdAt) < new Date(a.joined_collection))) {
      a.joined_collection = createdAt;
    }

    // Accepted counts and thumbs
    if (row.accepted) {
      acceptedByArtist.set(id, (acceptedByArtist.get(id) || 0) + 1);
      if (row.vcs?.cover_image) {
        if (!acceptedThumbs.has(id)) acceptedThumbs.set(id, []);
        if (acceptedThumbs.get(id).length < 6) {
          acceptedThumbs.get(id).push({ vc_id: row.vc_id, cover_image: row.vcs.cover_image });
        }
      }
    }
  }

  const list = Array.from(byId.values()).map((a) => ({
    ...a,
    accepted_count: acceptedByArtist.get(a.uuid) || 0,
    accepted_thumbs: acceptedThumbs.get(a.uuid) || [],
  }));
  return list;
}

export function useCollectionArtists(collectionId) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let ignore = false;
    async function load() {
      if (!collectionId) return;
      try {
        setLoading(true);
        setError(null);

        // 1) Get mapping rows with nested vcs -> users
        const { data: mappings, error: mapErr } = await supabase
          .from('collection_vc_mapping')
          .select(`
            vc_id,
            accepted,
            created_at,
            vcs (
              vc_id,
              cover_image,
              users (
                uuid,
                first_name,
                last_name,
                nickname,
                email,
                profile_img
              )
            )
          `)
          .eq('collection_id', collectionId)
          .order('created_at', { ascending: false });
        if (mapErr) throw mapErr;

        const baseArtists = buildArtistsFromMappings(mappings || []);
        const artistUuids = baseArtists.map((a) => a.uuid);

        // 2) Get submission ids for this collection (can be multiple)
        const { data: subs, error: subsErr } = await supabase
          .from('submissions_details')
          .select('submission_id')
          .eq('collection_id', collectionId);
        if (subsErr) throw subsErr;
        const submissionIds = (subs || []).map((s) => s.submission_id);

        // 3) Load acceptance mappings for these submissions
        let acceptanceMap = new Map();
        let paidSet = new Set();
        if (submissionIds.length) {
          const { data: acc, error: accErr } = await supabase
            .from('artist_submission_mapping')
            .select('user_id, accepted_at, acceptance_uuid, purchase_id')
            .in('submission_id', submissionIds);
          if (accErr) throw accErr;
          (acc || []).forEach((r) => {
            // If multiple records, prefer one with accepted_at set
            const prev = acceptanceMap.get(r.user_id);
            if (!prev || (r.accepted_at && !prev.accepted_at)) {
              acceptanceMap.set(r.user_id, { accepted_at: r.accepted_at, acceptance_uuid: r.acceptance_uuid });
            }
            if (r.purchase_id) paidSet.add(r.user_id);
          });
        }

        // 4) Has App flags (OneSignal player IDs)
        let appSet = new Set();
        if (artistUuids.length) {
          const { data: players } = await supabase
            .from('user_player_ids')
            .select('user_id')
            .in('user_id', artistUuids);
          (players || []).forEach((p) => appSet.add(p.user_id));
        }

        const enriched = baseArtists.map((a) => ({
          ...a,
          has_app: appSet.has(a.uuid),
          has_paid: paidSet.has(a.uuid),
          has_submitted_artwork: a.artworks_total > 0,
          acceptance: acceptanceMap.get(a.uuid) || null,
        }));

        if (!ignore) setItems(enriched);
      } catch (e) {
        if (!ignore) setError(e);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    load();
    return () => { ignore = true; };
  }, [collectionId]);

  const stats = useMemo(() => {
    const total = items.length;
    const acceptedArtists = items.filter((a) => a.accepted_count > 0).length;
    const withApp = items.filter((a) => a.has_app).length;
    const termsAccepted = items.filter((a) => a.acceptance?.accepted_at).length;
    return { total, acceptedArtists, withApp, termsAccepted };
  }, [items]);

  return { items, stats, loading, error };
}
